import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container } from "aurelia-framework";
import moment from "moment-timezone";
import { Loader } from "rsuite";
import { Client } from "../../api/client";
import { FlashService } from "../../flash/flash-service";
import confirm from "../../dialog/confirm";
import { OrderContextProvider } from "./helper/order-context";
import {getConfigOptions} from "./helper/config-model";
import {getStartingTimeDict} from "./helper/utility";
import {
  getOrderItemUrl,
  getOrderRouting,
  getDeleteOrderItemUrl,
  getAddOrderItemUrl,
  getOrderItemOptionalUrl,
  getOrderItemMoveUrl,
} from "./helper/url";

import "./offer.less";
import { GMapContextProvider } from "./helper/gmap-context";
import { OfferSearch } from "./views/offer-search";
import { EventAggregator } from "aurelia-event-aggregator";
import { StandardActions } from "../../action/standard-actions";
import { isMergedEvent } from "./helper/events-utilities";

const getPayload = (event) => {
  let fromDate;
  if (event.extendedProps.isAccommodation) {
    fromDate = moment(event.start).startOf("day").format("YYYY-MM-DD HH:mm:ss");
    const toDate = moment(event.end)
      .startOf("day")
      .format("YYYY-MM-DD HH:mm:ss");
    return {
      startDate: fromDate,
      endDate: toDate,
    };
  }

  fromDate = moment.utc(event.start).format("YYYY-MM-DD HH:mm:ss");
  return {
    startDate: fromDate,
  };
};

const isAccommodation = (item) =>
  item?.provider?.includes("tourism-accommodation") || item?.provider?.includes("tourism-ferry") ;

const Section = ({ children, className }) => {
  const ref = useRef();
  useEffect(() => {
    let offset;
    const setHeight = () => {
      const { top } = ref.current.getBoundingClientRect();
      const topOffset = top + window.scrollY;
      if (offset !== topOffset) {
        ref.current.style.setProperty("--top", `${topOffset}px`);
        offset = topOffset;
      }
    };
    const interval = setInterval(() => setHeight(), 300);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return (
    <section ref={ref} className={className}>
      {children}
    </section>
  );
};

export const Offer = ({ order }) => {
  const client = Container.instance.get(Client);
  const flash = Container.instance.get(FlashService);
  const ea = Container.instance.get(EventAggregator);

  const [offerModel, setOfferModel] = useState({
    firstDate: null,
    lastDate: "",
    participantsForm: 0,
    participantsOrder: order.b2bParticipants,
    optionDate: "",
    offerCancellationDate: "",
    roomsReserved: 0,
    duration: 1,
    offerCancellationTime: 1,
    overwriteOfferCancellationDate: false,
    overwriteOptionDate: false,
  });
  const [orderItems, setOrderItems] = useState([]);
  const [directions, setDirections] = useState(null);
  const [travelItems, setTravelItems] = useState([]);
  const [routingWarnings, setRoutingWarnings] = useState([]);
  const [wayPoints, setWayPoints] = useState([]);
  const [defaultDepartureTime, setDefaultDepartureTime] = useState(null);

  const [requestCounter, setRequestCounter] = useState(0);
  const [selectedDate, setSelectedDate] = useState();
  const [dayStartTimes, setDayStartTime] = useState(
    getStartingTimeDict(order?.startingTimes ?? [])
  );
  const [offerStartTime, setOfferStartTime] = useState(null);
  const [offerEndTime, setOfferEndTime] = useState(null);
  const [dragInProgress, setDragInProgress] = useState(false);

  const showErrorMessage = useCallback(
    (message, timeout = 20000) =>
      flash.flash("error", message, null, {
        timeOut: timeout,
      }),
    [flash]
  );

  const runRequest = useCallback(
    async (request, errMessage) => {
      setRequestCounter((counter) => counter + 1);
      let res, error;
      try {
        res = await request();
        error = false;
      } catch (error) {
        console.error(error);
        showErrorMessage(
          error?.data?.localizedMessage ||
            error?.data?.message ||
            error?.message ||
            errMessage
        );
        res = null;
        error = true;
      } finally {
        setRequestCounter((counter) => (counter > 0 ? counter - 1 : 0));
      }
      return [res, error];
    },
    [showErrorMessage]
  );

  const fetchItems = async () => {
    try {
      const response = await client.get(getOrderItemUrl(order));

      setOrderItems(response.items);
    } catch (error) {
      showErrorMessage(
        error?.data?.localizedMessage ?? "Leistungen konnten nicht geladen werden"
      );
    }

    try {
      const routing = await client.get(getOrderRouting(order));

      setTravelItems(routing?.travelItems ?? []);
      setWayPoints(routing?.wayPoints ?? []);
      setRoutingWarnings(routing?.routingWarnings ?? []);
      setDirections(routing?.geoJson);
      setDefaultDepartureTime(routing?.defaultDepartureTime);
    } catch (error) {
      showErrorMessage(
        error?.data?.localizedMessage ?? "Route konnte nicht geladen werden"
      );
    }
  };

  const onDateChange = useCallback((date) => {
    const firstDate = moment(date);
    setSelectedDate(firstDate);
    setOfferModel((previousValue) => ({
      ...previousValue,
      firstDate,
    }));
  }, []);

  const selectNextAccommodationFreeDay = useCallback(
    (accStart, accEnd) => {
      let dateToCheck = moment(accStart).startOf("day");
      const accommodations = orderItems.filter(isAccommodation).map((i) => {
        const startDate = moment(i.fromDate).startOf("day");
        const endDate = i?.toDate
          ? moment(i.toDate).startOf("day")
          : moment(startDate).add(1, "day");
        return [startDate, endDate];
      });
      accommodations.push([
        accStart,
        moment(accEnd).startOf("day").add(1, "day"),
      ]);
      const hasAccommodation = (date) =>
        accommodations.some(([startDate, endDate]) =>
          date.isBetween(startDate, endDate, "day", "[)")
        );
      while (hasAccommodation(dateToCheck)) {
        dateToCheck = dateToCheck.add("day", 1);
        if (offerEndTime.isSameOrBefore(dateToCheck, "day")) {
          dateToCheck = offerStartTime.clone();
          break;
        }
      }
      onDateChange(dateToCheck);
    },
    [selectedDate, onDateChange, orderItems, offerStartTime, offerEndTime]
  );

  const onDeleteEvent = useCallback(async (event) => {
    const ids = isMergedEvent(event)
      ? event.extendedProps.mergedEvents
      : [event.id];
    const removeEvent = async (id) => {
      const url = getDeleteOrderItemUrl(order, id);
      await client.remove(url);
    };
    confirm("Event löschen", "Möchten Sie dieses Event löschen?").then(
      async () => {
        const remove = async () => {
          await Promise.all(ids.map(removeEvent));
          await fetchItems();
          ea.publish("sio_form_post_submit", {
            config: { modelId: "order/order" },
          });
        };
        await runRequest(remove, "Event delete error");
      },
      () => {
        /* prevent unhandled rejection message */
      }
    );
  }, []);

  const updateEventItem = useCallback(
    async (data) => {
      const payload = {
        ...data,
      };
      const url = getAddOrderItemUrl(order);
      const update = async () => {
        const create = async () => await client.patch(url, payload);
        if (isAccommodation(payload)) {
          const lastButOneDay = moment(offerEndTime).subtract(1, "day");
          const accStart = moment(payload.fromDate).startOf("day");
          const accEnd = moment(payload.toDate).subtract(1, "day").endOf("day");
          const isEndAfter = accEnd.isAfter(lastButOneDay, "day");
          if (isEndAfter && payload?.provider?.includes("tourism-accommodation")) {
            showErrorMessage(
              "Eine Unterkunft am letzten Tag der Reise kann nicht gebucht werden"
            );
            return;
          }
          await create();
          const isOnLastButOneDay = lastButOneDay.isSame(accEnd, "day");
          isOnLastButOneDay
            ? onDateChange(offerStartTime)
            : selectNextAccommodationFreeDay(accStart, accEnd);
        } else {
          await create();
        }
        await fetchItems();
        ea.publish("sio_form_post_submit", {
          config: { modelId: "order/order" },
        });
      };
      await runRequest(update, "Event update error");
    },
    [selectNextAccommodationFreeDay, offerEndTime, offerStartTime]
  );

  const setConfigOptions = () => {
    const isPeriodSet = order.offerStartDate && order.offerEndDate;
    if (!isPeriodSet) {
      showErrorMessage(
        "Bitte setzen Sie zuerst den Angebotszeitraum, um eine Reise zu konfigurieren"
      );
    }
    const model = getConfigOptions(order, orderItems, offerModel);
    setOfferStartTime(model.firstDate);
    setOfferEndTime(model.lastDate);
    setOfferModel(model);
    console.info("offer model", model.firstDate);
    setSelectedDate(model.firstDate);
  };

  useEffect(() => {
    setConfigOptions();
    fetchItems();
  }, [order.id]);
  const onOptionalChange = async (isOptional, event) => {
    const ids = isMergedEvent(event, (e) => e.mergedEvents)
      ? event.mergedEvents
      : [event.id];
    const updateEvent = async (id) => {
      const url = getOrderItemOptionalUrl(id);
      await client.put(url, {
        optional: isOptional,
      });
    };
    const update = async () => {
      await Promise.all(ids.map(updateEvent));
      await fetchItems();
    };
    await runRequest(update, "Optional change error");
  };

  const onEventDrag = async (event) => {
    setDragInProgress(true)
    const ids = isMergedEvent(event)
      ? event.extendedProps.mergedEvents
      : [event.id];
    const updateEventOnDrag = async (id) => {
      const url = getOrderItemMoveUrl(order, id);
      const payload = getPayload(event);
      await client.patch(url, payload);
    };
    const update = async () => {
      await Promise.all(ids.map(updateEventOnDrag));
      await fetchItems();
    };
    await runRequest(update, "Event drag error");
    setDragInProgress(false)

  };

  const updateDateStartTime = async (date, time) => {
    const payload = {
      startingTimes: Object.entries({
        ...dayStartTimes,
        [date]: time,
      })
        .map(([date, time]) => ({date, time})),
    };
    const url = `tourism-order/modify-starting-time/${order.id}`;
    const update = async () => await client.put(url, payload);
    const [res] = await runRequest(update, "Set start time for date error");
    setDayStartTime(getStartingTimeDict(res?.data?.startingTimes ?? []));

    await fetchItems();
  };

  const addArticleOnDateClick = useCallback((config, context) => {
    const actions = Container.instance.get(StandardActions);
    setRequestCounter((r) => r + 1);
    actions
      .getAction(config, context)
      .action()
      .then(
        () => setRequestCounter((counter) => (counter > 0 ? counter - 1 : 0)),
        () => setRequestCounter((counter) => (counter > 0 ? counter - 1 : 0))
      );
  }, []);

  const onEditArticle = useCallback((event) => {
    const actions = Container.instance.get(StandardActions);
    const eventId = event.id;
    actions
      .getAction(
        {
          type: "view",
          viewId: "order/order-item-edit",
          modal: true,
        },
        {
          id: [eventId],
          modal: "order/order-item",
          title: event?.name || "Event Edit",
        }
      )
      .action().then(async ()=>{
        await fetchItems();
        ea.publish("sio_form_post_submit", {
          config: { modelId: "order/order" },
        });
      });
  }, []);


  return (
    <GMapContextProvider>
      <Section className="offer-configurator">
        {requestCounter > 0 && <Loader className="offer-loader" />}
        <OrderContextProvider
          value={{
            directions,
            travelItems,
            wayPoints,
            defaultDepartureTime,
            dayStartTimes,
            updateDateStartTime,
            onDateChange,
            onDeleteEvent,
            updateEventItem,
            routingWarnings,
            onOptionalChange,
            onEventDrag,
            orderItems,
            order,
            participantsOrder: offerModel.participantsOrder,
            requestCounter,
            showErrorMessage,
            client,
            flash,
            selectedDate,
            setSelectedDate,
            addArticleOnDateClick,
            offerStartTime,
            offerEndTime,
            onEditArticle,
            dragInProgress
          }}
        >
          <OfferSearch
            items={orderItems}
            directions={directions}
            firstDate={offerModel.firstDate}
            lastDate={offerModel.lastDate}
            participants={offerModel.participantsOrder}
            fromDate={order.fromDate}
            toDate={order.toDate}
            offerStartDate={order.offerStartDate}
            offerEndDate={order.offerEndDate}
            minHotelCategory={order.minHotelCategory}
            accommodationDisplay={order.accommodationDisplay}
            minFare={order.minFare}
          />
        </OrderContextProvider>
      </Section>
    </GMapContextProvider>
  );
};

export default Offer;
